import * as React from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import type { FeatureName } from '@/api/features';
import {
  HomeIcon, BookOpenIcon, CheckCircleIcon, FolderIcon,
  LightBulbIcon, ShareIcon, UserCircleIcon,
} from '@heroicons/react/24/outline';

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  feature?: FeatureName;
  /** Hidden on phone/installed-PWA (e.g. desktop-only graph view). */
  desktopOnly?: boolean;
}

const items: NavItem[] = [
  { name: 'Home', path: '/home', icon: HomeIcon },
  { name: 'Knowledge', path: '/knowledge', icon: BookOpenIcon },
  { name: 'Tasks', path: '/tasks', icon: CheckCircleIcon },
  { name: 'Projects', path: '/projects', icon: FolderIcon },
  { name: 'Suggestions', path: '/suggestions', icon: LightBulbIcon, feature: 'suggestions' },
  { name: 'Connections', path: '/connections', icon: ShareIcon, feature: 'graph_view', desktopOnly: true },
  { name: 'Profile', path: '/profile', icon: UserCircleIcon },
];

interface BrowseNavProps {
  onNavigate: () => void;
}

/** True on phones / installed PWA — used to hide desktop-only surfaces. */
function useIsCompact(): boolean {
  const [compact, setCompact] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = () => setCompact(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return compact;
}

export function BrowseNav({ onNavigate }: BrowseNavProps) {
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const { isEnabled } = useFeatureFlags();
  const isCompact = useIsCompact();
  const visible = items.filter(
    (i) => (!i.feature || isEnabled(i.feature)) && !(isCompact && i.desktopOnly)
  );

  const isActive = (path: string) => currentPath.startsWith(path);

  return (
    <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label="Browse">
      <ul className="space-y-0.5">
        {visible.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <li key={item.path}>
              <Link
                to={item.path}
                onClick={onNavigate}
                className={`flex items-center gap-3 rounded-xl px-4 py-2.5 text-[13px] transition-colors ${
                  active ? 'bg-primary/10 font-semibold text-primary' : 'text-text-secondary hover:bg-surface-container-high/50 hover:text-text-primary'
                }`}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {item.name}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
